import * as React from "react";
import { Plus, Trash2 } from "Components/ui/icons";

import {
  Alert,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  FormField,
  IconButton,
  Input,
  Select,
  Textarea,
  notify,
  useStableId,
} from "Components/ui";
import { getApiErrorMessage } from "api/apiError";
import { Client, ClientFormValues, ClientPayload } from "./types";

const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "disabled", label: "Disabled" },
  { value: "not_working", label: "Not working" },
] as const;

// Stable row keys: removing a middle contact must not shift focus or input state to the next row.
let contactKeySeq = 0;
const nextContactKey = (): string => {
  contactKeySeq += 1;
  return `contact-${contactKeySeq}`;
};

const emptyContact = (): ClientFormValues["contacts"][number] => ({
  key: nextContactKey(),
  name: "",
  role: "",
  email: "",
  phone: "",
});

const toFormValues = (client: Client | null): ClientFormValues => ({
  name: client ? client.name : "",
  uri: client ? client.uri : "",
  server: client && client.deployment ? client.deployment.server : "",
  app: client && client.deployment ? client.deployment.app : "",
  port: client && client.deployment && client.deployment.port ? String(client.deployment.port) : "",
  slug: client ? client.slug : "",
  lifecycleStatus: client ? client.lifecycleStatus : "active",
  contacts: (client ? client.contacts : []).map((contact) => ({
    key: nextContactKey(),
    name: contact.name || "",
    role: contact.role || "",
    email: contact.email || "",
    phone: contact.phone || "",
  })),
  notes: client && client.notes ? client.notes : "",
});

type Errors = Partial<Record<"name" | "uri" | "server" | "app" | "port" | "slug", string>>;

const validate = (values: ClientFormValues): Errors => {
  const errors: Errors = {};
  if (!values.name.trim()) errors.name = "Enter the client's name.";
  if (!values.uri.trim()) errors.uri = "Enter the TeleOpd address, e.g. dev.metahos.com.";
  const app = values.app.trim().toLowerCase();
  const server = values.server.trim();
  if (app && !SLUG_PATTERN.test(app)) {
    errors.app = "Use the pm2 app name: lowercase letters, digits and hyphens.";
  }
  if ((server && !app) || (app && !server)) {
    errors[server ? "app" : "server"] = "Enter both the server and the pm2 app.";
  }
  if (values.port.trim()) {
    const port = Number(values.port);
    if (!Number.isInteger(port) || port < 1 || port > 65535) {
      errors.port = "Port must be a number from 1 to 65535.";
    }
  }
  const slug = values.slug.trim().toLowerCase();
  if (!slug && !app) {
    errors.slug = "Enter the pm2 app (or a slug).";
  } else if (slug && (!SLUG_PATTERN.test(slug) || slug.length < 2 || slug.length > 40)) {
    errors.slug = "2-40 lowercase letters, digits and single hyphens.";
  }
  return errors;
};

const toPayload = (values: ClientFormValues): ClientPayload => {
  const app = values.app.trim().toLowerCase();
  const server = values.server.trim().toLowerCase();
  return {
    name: values.name.trim(),
    uri: values.uri.trim(),
    slug: values.slug.trim().toLowerCase() || app || undefined,
    deployment:
      app && server ? { server, app, port: values.port ? Number(values.port) : null } : null,
    lifecycleStatus: values.lifecycleStatus,
    contacts: values.contacts
      .map((contact) => ({
        name: contact.name.trim() || undefined,
        role: contact.role.trim() || undefined,
        email: contact.email.trim() || undefined,
        phone: contact.phone.trim() || undefined,
      }))
      .filter((contact) => contact.name || contact.email || contact.phone),
    notes: values.notes.trim(),
  };
};

interface ClientFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** null = create. */
  client: Client | null;
  /** Linked or billed clients keep their slug (the server refuses a change too). */
  slugLocked?: boolean;
  /** Server values already in use, offered as suggestions. */
  knownServers: string[];
  saving: boolean;
  onSubmit: (payload: ClientPayload) => Promise<unknown>;
}

const ClientFormDialog: React.FC<ClientFormDialogProps> = ({
  open,
  onOpenChange,
  client,
  slugLocked = false,
  knownServers,
  saving,
  onSubmit,
}) => {
  const [values, setValues] = React.useState<ClientFormValues>(() => toFormValues(client));
  const [errors, setErrors] = React.useState<Errors>({});
  const [serverError, setServerError] = React.useState<string | null>(null);
  // The slug follows the pm2 app until someone types in it.
  const [slugTouched, setSlugTouched] = React.useState<boolean>(Boolean(client));
  const serversListId = useStableId("client-servers");

  React.useEffect(() => {
    if (open) {
      setValues(toFormValues(client));
      setErrors({});
      setServerError(null);
      setSlugTouched(Boolean(client));
    }
  }, [open, client]);

  const set = <K extends keyof ClientFormValues>(key: K, value: ClientFormValues[K]) =>
    setValues((current) => {
      const next = { ...current, [key]: value };
      if (key === "app" && !slugTouched) {
        next.slug = String(value).trim().toLowerCase();
      }
      return next;
    });

  const setContact = (key: string, field: "name" | "role" | "email" | "phone", value: string) =>
    setValues((current) => ({
      ...current,
      contacts: current.contacts.map((contact) =>
        contact.key === key ? { ...contact, [field]: value } : contact
      ),
    }));

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const nextErrors = validate(values);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }
    setServerError(null);
    try {
      await onSubmit(toPayload(values));
      onOpenChange(false);
    } catch (err) {
      setServerError(getApiErrorMessage(err, "The client could not be saved."));
      notify.error(err);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <form onSubmit={handleSubmit} noValidate>
          <DialogHeader
            title={client ? `Edit ${client.name}` : "Add tenant"}
            description={
              client
                ? "Changes apply to SAP's record of this TeleOpd deployment."
                : "One TeleOpd deployment. New tenants start Locked until you move them to Actionable."
            }
          />
          <DialogBody className="space-y-5">
            {serverError && <Alert tone="danger">{serverError}</Alert>}

            <div className="grid gap-4 sm:grid-cols-2">
              <FormField label="Name" required error={errors.name} className="sm:col-span-2">
                <Input
                  value={values.name}
                  onChange={(event) => set("name", event.target.value)}
                  placeholder="As in the instance's config title"
                  autoFocus
                />
              </FormField>
              <FormField
                label="TeleOpd address"
                required
                error={errors.uri}
                hint="The public URL that serves /config. Only the origin is kept."
                className="sm:col-span-2"
              >
                <Input
                  value={values.uri}
                  onChange={(event) => set("uri", event.target.value)}
                  placeholder="https://dev.metahos.com"
                  className="font-mono"
                />
              </FormField>
              <FormField
                label="Server"
                error={errors.server}
                hint="IP or hostname the pm2 app runs on."
              >
                <Input
                  value={values.server}
                  onChange={(event) => set("server", event.target.value)}
                  placeholder="159.65.145.41"
                  list={serversListId}
                  className="font-mono"
                />
              </FormField>
              <datalist id={serversListId}>
                {knownServers.map((server) => (
                  <option key={server} value={server} />
                ))}
              </datalist>
              <div className="grid grid-cols-3 gap-3">
                <FormField label="pm2 app" error={errors.app} className="col-span-2">
                  <Input
                    value={values.app}
                    onChange={(event) => set("app", event.target.value)}
                    placeholder="develop"
                    className="font-mono"
                  />
                </FormField>
                <FormField label="Port" error={errors.port}>
                  <Input
                    value={values.port}
                    onChange={(event) => set("port", event.target.value.replace(/[^0-9]/g, ""))}
                    placeholder="38000"
                    inputMode="numeric"
                    className="font-mono"
                  />
                </FormField>
              </div>
              <FormField
                label="Slug"
                error={errors.slug}
                hint={
                  slugLocked
                    ? "Fixed: this client has a TeleOpd connection or billing records."
                    : "Unique handle; defaults to the pm2 app name."
                }
              >
                <Input
                  value={values.slug}
                  onChange={(event) => {
                    setSlugTouched(true);
                    set("slug", event.target.value.toLowerCase());
                  }}
                  placeholder="develop"
                  disabled={slugLocked}
                  className="font-mono"
                />
              </FormField>
              <FormField label="Status">
                <Select
                  value={values.lifecycleStatus}
                  onValueChange={(value) => set("lifecycleStatus", value)}
                  options={STATUS_OPTIONS.map((option) => ({ ...option }))}
                />
              </FormField>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">Contacts</p>
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  leftIcon={<Plus />}
                  onClick={() =>
                    setValues((current) => ({
                      ...current,
                      contacts: [...current.contacts, emptyContact()],
                    }))
                  }
                >
                  Add contact
                </Button>
              </div>
              {values.contacts.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  No contacts yet. Billing reminders go to contacts that have an email.
                </p>
              )}
              {values.contacts.map((contact) => (
                <div
                  key={contact.key}
                  className="grid items-end gap-3 sm:grid-cols-[1fr_1fr_1.3fr_1fr_auto]"
                >
                  <FormField label="Name">
                    <Input
                      value={contact.name}
                      onChange={(e) => setContact(contact.key, "name", e.target.value)}
                    />
                  </FormField>
                  <FormField label="Role">
                    <Input
                      value={contact.role}
                      onChange={(e) => setContact(contact.key, "role", e.target.value)}
                      placeholder="Accounts"
                    />
                  </FormField>
                  <FormField label="Email">
                    <Input
                      type="email"
                      value={contact.email}
                      onChange={(e) => setContact(contact.key, "email", e.target.value)}
                    />
                  </FormField>
                  <FormField label="Phone">
                    <Input
                      type="tel"
                      value={contact.phone}
                      onChange={(e) => setContact(contact.key, "phone", e.target.value)}
                    />
                  </FormField>
                  <IconButton
                    type="button"
                    label="Remove contact"
                    icon={<Trash2 />}
                    variant="danger-ghost"
                    onClick={() =>
                      setValues((current) => ({
                        ...current,
                        contacts: current.contacts.filter((item) => item.key !== contact.key),
                      }))
                    }
                  />
                </div>
              ))}
            </div>

            <FormField label="Notes">
              <Textarea
                value={values.notes}
                onChange={(event) => set("notes", event.target.value)}
                rows={3}
              />
            </FormField>
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" loading={saving}>
              {client ? "Save changes" : "Add tenant"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ClientFormDialog;
